// The live library, for reads that follow it. The library is re-read only
// when the workspace's log moved (`shouldRefetchState`; the head stream
// sends at most one move a second), and a resource's row in it changes only
// when something the row shows about that resource changed: its last edit,
// grounding, read status, facts, what uses it. A read of one resource keys on
// its row (`rowVersion`), so it re-reads exactly when that row moved and never
// on a log move elsewhere: no read storms, and an unchanged read never runs.

import { createContext, useContext } from "react";
import type { Library, LibraryItem } from "../ports.js";

const LiveLibraryContext = createContext<Library | null>(null);

export const LiveLibraryProvider = LiveLibraryContext.Provider;

const rowsOf = (library: Library): readonly LibraryItem[] => [
    ...library.sources,
    ...library.materials,
    ...library.exports,
    ...library.files,
    ...library.archived,
];

// The resource's version: its whole library row. Undefined: no library yet,
// or no row for the id. Pure and total.
export const rowVersion = (
    library: Library | null,
    id: string | null,
): string | undefined => {
    const row =
        library === null || id === null
            ? undefined
            : rowsOf(library).find((r) => r.id === id);
    return row === undefined ? undefined : JSON.stringify(row);
};

// The row version of `id` in the live library above this component.
export const useRowVersion = (id: string): string | undefined =>
    rowVersion(useContext(LiveLibraryContext), id);
